import { normalizeBackendCaptureReviewRows } from '../../../utils/naturalCaptureReviewNormalization';
import { buildConciseBehaviorTitle } from '../../../utils/behaviorTitles';
import { buildConciseMilestoneTitle } from '../../../utils/milestoneTitles';
import { buildImportCandidateRow } from './importLogsCandidateRowBuilders';
import { recoverMissingLocalRowsFromAISegments } from './importLogsAISegmentReconciliation';
import { resolveDominantCategoryOverlaps } from './importLogsDominantCategoryResolution';
import { CARE_EXPLICIT_TIME_PATTERN } from '../../../shared/careDateTimePatterns';

const buildAITimelineSegmentationRequest = ({
  text = '',
  defaultDate = new Date(),
  childName = null,
  localRows = [],
  debugContext = {},
} = {}) => ({
  text: String(text || '').trim(),
  mode: 'caregiver_timeline_segmentation',
  fallbackDate: defaultDate instanceof Date ? defaultDate.toISOString() : defaultDate,
  childName: childName || null,
  localRows: Array.isArray(localRows)
    ? localRows.map((row) => ({
        category: row?.category || null,
        title: row?.title || '',
        note: row?.note || row?.text || row?.body || '',
        localTime: row?.localTime || null,
        date: row?.date instanceof Date ? row.date.toISOString() : row?.date || null,
      }))
    : [],
  debugContext: debugContext && typeof debugContext === 'object' ? debugContext : {},
});

const resolveSegmentDate = (segment = {}) => (
  segment?.isoDateTime
  || segment?.dateTime
  || segment?.date
  || segment?.isoDate
  || null
);

const resolveSegmentText = (segment = {}) => String(
  segment?.segmentText
  || segment?.note
  || segment?.text
  || segment?.body
  || ''
).trim();

const sentenceCase = (value = '') => {
  const text = String(value || '').trim().replace(/\s+/g, ' ');
  if (!text) return '';
  return `${text[0].toUpperCase()}${text.slice(1)}`;
};

const firstClause = (value = '') => String(value || '')
  .trim()
  .replace(/\s+/g, ' ')
  .split(/(?<=[.!?])\s+/)[0]
  ?.split(/\s*[,:;]\s+/)[0]
  ?.trim() || '';

const buildMedicationSegmentTitleHint = (text = '') => {
  const source = String(text || '').trim();
  if (!source) return 'Medication';

  const reverseStatusMatch = source.match(/\b([A-Z][a-zA-Z]+)\s+(stopped|started|restarted|held|missed)\b/i);
  if (reverseStatusMatch) {
    const med = sentenceCase(reverseStatusMatch[1]);
    const action = reverseStatusMatch[2].toLowerCase();
    return `${med} ${action}`;
  }

  const statusMatch = source.match(/\b(increased|decreased|stopped|started|restarted|held|missed)\s+([A-Z][a-zA-Z]+)/i);
  if (statusMatch) {
    const action = sentenceCase(statusMatch[1]);
    const med = sentenceCase(statusMatch[2]);
    return `${action} ${med}`;
  }

  const doseTimeMatch = source.match(/\b([A-Z][a-zA-Z]+)\s+(Morning|Evening)\s+Dose\b/i);
  if (doseTimeMatch) {
    return `${sentenceCase(doseTimeMatch[1])} ${sentenceCase(doseTimeMatch[2])} dose`;
  }

  const medNameMatch = source.match(/\b([A-Z][a-zA-Z]+)\b/);
  if (medNameMatch) {
    return sentenceCase(medNameMatch[1]);
  }

  return 'Medication';
};

const buildSleepSegmentTitleHint = (text = '') => {
  const source = String(text || '').trim();
  if (!source) return 'Sleep';
  const wakeTimeMatch = source.match(new RegExp(`\\bwakes?\\s+up\\s+at\\s+(${CARE_EXPLICIT_TIME_PATTERN})\\b`, 'i'));
  if (wakeTimeMatch?.[1]) return `Woke up at ${wakeTimeMatch[1].toUpperCase().replace(/\s+/g, ' ')}`;
  if (/\bwakes?\s+up\b/i.test(source)) return 'Woke up';
  if (/\bsleep duration\b/i.test(source)) return 'Sleep duration';
  if (/\bfell asleep\b/i.test(source)) return 'Fell asleep';
  return sentenceCase(firstClause(source)) || 'Sleep';
};

const buildHealthSegmentTitleHint = (text = '') => {
  const source = String(text || '').trim();
  if (!source) return 'Health';
  if (/\bappointment\b/i.test(source)) {
    if (/\bneurolog(?:y|ist)\b/i.test(source)) return 'Neurology appointment';
    if (/\bpsychiatr(?:y|ist)\b/i.test(source)) return 'Psychiatry appointment';
    if (/\bdoctor\b/i.test(source)) return 'Doctor appointment';
    return 'Appointment';
  }
  const symptomMatch = source.match(/\b(sleepy|groggy|sluggish|tired|fatigued|dizzy|nauseous|nausea)\b(?:,\s*\b(sleepy|groggy|sluggish|tired|fatigued|dizzy|nauseous|nausea)\b)?/i);
  if (symptomMatch?.[1]) {
    const first = sentenceCase(symptomMatch[1].toLowerCase());
    const second = symptomMatch[2] ? symptomMatch[2].toLowerCase() : '';
    return second ? `${first} and ${second}` : first;
  }
  return sentenceCase(firstClause(source)) || 'Health';
};

const buildActivitySegmentTitleHint = (text = '') => {
  const source = String(text || '').trim();
  if (!source) return 'Activity';
  return sentenceCase(firstClause(source)) || 'Activity';
};

const buildDailySegmentTitleHint = (text = '') => {
  const source = String(text || '').trim();
  if (!source) return 'Daily log';
  return sentenceCase(firstClause(source)) || 'Daily log';
};

const normalizeAITimelineCategoryHint = (value = '') => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return 'other';

  switch (normalized) {
    case 'meds':
    case 'med':
    case 'medicine':
      return 'medication';
    case 'milestone':
    case 'win':
      return 'milestone';
    case 'mood':
      return 'mood';
    case 'log':
    case 'note':
    case 'other':
      return 'other';
    default:
      return normalized;
  }
};

const deriveAITimelineTitleHint = (categoryHint = '', text = '') => {
  const normalizedCategory = String(categoryHint || '').trim().toLowerCase();
  const source = String(text || '').trim();

  switch (normalizedCategory) {
    case 'behavior':
      return buildConciseBehaviorTitle(source);
    case 'medication':
      return buildMedicationSegmentTitleHint(source);
    case 'sleep':
      return buildSleepSegmentTitleHint(source);
    case 'milestone':
      return buildConciseMilestoneTitle(source);
    case 'health':
      return buildHealthSegmentTitleHint(source);
    case 'activity':
      return buildActivitySegmentTitleHint(source);
    case 'daily':
    case 'other':
    default:
      return buildDailySegmentTitleHint(source);
  }
};

export const normalizeAITimelineSegmentsToReviewRows = ({
  responseData = {},
  rawText = '',
  parserMode = 'ai',
  sourceMode = 'ai_segmentation',
  inputMode = 'import',
} = {}) => {
  const segments = Array.isArray(responseData?.segments)
    ? responseData.segments
    : (Array.isArray(responseData?.entries) ? responseData.entries : []);

  const baseRows = segments
    .map((segment, segmentIndex) => {
      const segmentText = resolveSegmentText(segment);
      if (!segmentText) {
        return null;
      }
      const hintedCategory = String(
        segment?.categoryHint
        || segment?.category
        || segment?.cat
        || 'log'
      ).trim().toLowerCase();
      const normalizedHintedCategory = normalizeAITimelineCategoryHint(hintedCategory);
      const category = normalizedHintedCategory || 'other';
      const titleHint = deriveAITimelineTitleHint(category, segmentText);
      const sourceBlockId = String(
        segment?.sourceBlockId
        || segment?.metadata?.sourceBlockId
        || `ai_segmentation:${segmentIndex}`
      ).trim();

      return buildImportCandidateRow({
        category,
        title: '',
        titlePrefix: '',
        note: segmentText,
        text: segmentText,
        body: segmentText,
        content: segmentText,
        date: resolveSegmentDate(segment),
        time: String(segment?.timeLabel || segment?.time || segment?.localTime || '').trim() || null,
        localTime: String(segment?.timeLabel || segment?.time || segment?.localTime || '').trim() || null,
        confidence: String(segment?.confidence || 'medium').trim().toLowerCase() || 'medium',
        parsedObservation: true,
        parsedSourceCategory: hintedCategory || 'log',
        sourceText: rawText,
        originalText: rawText,
        parserMode,
        sourceMode,
        metadata: {
          timelineSegmentationFallback: true,
          sourceAnchorText: String(segment?.sourceAnchorText || '').trim(),
          aiSegmentationConfidence: String(segment?.confidence || 'medium').trim().toLowerCase() || 'medium',
          aiOriginalCategoryHint: hintedCategory || 'log',
          aiCategoryHint: category,
          dateLabel: String(segment?.dateLabel || '').trim(),
          dateHeading: String(segment?.sourceAnchorText || segment?.dateLabel || '').trim(),
          dateSource: 'ai_timeline_segmentation',
          parseWarnings: Array.isArray(responseData?.parseWarnings) ? responseData.parseWarnings : [],
          uncertainSegments: Array.isArray(responseData?.uncertainSegments) ? responseData.uncertainSegments : [],
        },
      }, {
        rawSourceText: segmentText,
        sourceBlockId,
        sourceSentenceIndex: Number.isInteger(segment?.sourceSentenceIndex) ? segment.sourceSentenceIndex : segmentIndex,
        detectedDate: resolveSegmentDate(segment),
        detectedTime: String(segment?.timeLabel || segment?.time || segment?.localTime || '').trim() || null,
        categoryHint: normalizedHintedCategory,
        titleHint,
        parserMode,
        sourceMode,
      });
    })
    .filter(Boolean);

  return resolveDominantCategoryOverlaps(normalizeBackendCaptureReviewRows(baseRows, {
    parserMode,
    sourceMode,
    inputMode,
    rawText,
    backendSource: responseData?.source || responseData?.parsedBy || 'caregiver_timeline_segmentation',
    parsedBy: responseData?.parsedBy || responseData?.source || 'caregiver_timeline_segmentation',
    reviewNeeded: true,
  }));
};

export const segmentImportedCaregiverTimelineWithAI = async ({
  text = '',
  defaultDate = new Date(),
  childName = null,
  localRows = [],
  debugContext = {},
  callable,
  parserMode = 'ai',
  sourceMode = 'ai_segmentation',
  inputMode = 'import',
} = {}) => {
  if (typeof callable !== 'function') {
    return {
      rows: [],
      responseData: {},
      importDebug: {
        aiTimelineSegmentationTriggered: true,
        aiTimelineSegmentCount: 0,
        timelineSegmentationReason: debugContext?.reasons || [],
      },
    };
  }

  const response = await callable(buildAITimelineSegmentationRequest({
    text,
    defaultDate,
    childName,
    localRows,
    debugContext,
  }));
  const responseData = response?.data || {};
  if (responseData?.error) {
    const timelineError = new Error(
      String(responseData?.debugReason || responseData?.error || 'AI timeline segmentation failed').trim()
      || 'AI timeline segmentation failed'
    );
    timelineError.responseData = responseData;
    timelineError.debugRawPreview = String(responseData?.debugRawPreview || '').trim();
    throw timelineError;
  }
  const rows = normalizeAITimelineSegmentsToReviewRows({
    responseData,
    rawText: text,
    parserMode,
    sourceMode,
    inputMode,
  });
  const recoveredRows = recoverMissingLocalRowsFromAISegments({
    localRows,
    aiRows: rows,
  });

  return {
    rows: recoveredRows,
    responseData,
    importDebug: {
      aiTimelineSegmentationTriggered: true,
      aiTimelineSegmentCount: recoveredRows.length,
      timelineSegmentationReason: debugContext?.reasons || [],
    },
  };
};
